"use client";

import type React from "react";
import { DEFAULT_OTP_LENGTH, RESEND_OTP_INTERVAL_SECONDS } from "@/config";
import CButton from "@/Shared/CButton/CButton";
import OtpInput from "@/Shared/Form/OtpInput";
import InfoBanner from "./InfoBanner";

interface Props {
  email: string;
  otp: string;
  newPassword: string;
  confirmPassword: string;
  setOtp: (v: string) => void;
  setNewPassword: (v: string) => void;
  setConfirmPassword: (v: string) => void;
  onVerify: () => void;
  onOtpComplete?: (v: string) => void;
  onResend?: () => void;
  secondsRemaining?: number;
  loading?: boolean;
  infoMessage?: string | null;
  onDismissInfo?: () => void;
  onChangeEmail?: () => void;
  otpVerified?: boolean;
  otpLength?: number;
}

/**
 * VerifyStep — OTP input + new password fields + reset button.
 */
const VerifyStep: React.FC<Props> = ({
  email,
  otp,
  newPassword,
  confirmPassword,
  setOtp,
  setNewPassword,
  setConfirmPassword,
  onVerify,
  onOtpComplete,
  onResend,
  secondsRemaining = 0,
  loading = false,
  infoMessage,
  onDismissInfo,
  onChangeEmail,
  otpVerified = false,
  otpLength = DEFAULT_OTP_LENGTH,
}) => {
  return (
    <>
      <InfoBanner
        message={infoMessage ?? null}
        onDismiss={() => onDismissInfo?.()}
        onResend={onResend}
        secondsRemaining={secondsRemaining}
        totalSeconds={RESEND_OTP_INTERVAL_SECONDS}
        loading={loading}
      />

      <p className="mb-4 text-sm text-muted-foreground">
        We sent an OTP to <strong>{email}</strong>. Enter it below with your new
        password.
      </p>

      <div className="mb-4 flex flex-col items-center">
        <div className="flex justify-center">
          <OtpInput
            length={otpLength}
            value={otp}
            onChange={(v) => setOtp(v)}
            onComplete={(v) => onOtpComplete?.(v)}
            autoFocus
            aria-label="otp-group"
          />
        </div>
        {otpVerified && (
          <p className="mt-2 text-sm text-emerald-600">OTP verified ✅</p>
        )}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          onVerify();
        }}
        className="space-y-4"
      >
        <div>
          <label
            htmlFor="reset-new-password"
            className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            New Password
          </label>
          <input
            id="reset-new-password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Enter new password"
            required
            minLength={8}
            autoComplete="new-password"
            disabled={loading}
            className="w-full px-4 py-3 border-2 border-transparent rounded-lg bg-[#365DF8]/10 focus:border-[#365DF8] focus:bg-[#365DF8]/10 outline-none transition text-sm dark:bg-gray-900 disabled:opacity-50"
          />
        </div>

        <div>
          <label
            htmlFor="reset-confirm-password"
            className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Confirm Password
          </label>
          <input
            id="reset-confirm-password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm new password"
            required
            minLength={8}
            autoComplete="new-password"
            disabled={loading}
            className="w-full px-4 py-3 border-2 border-transparent rounded-lg bg-[#365DF8]/10 focus:border-[#365DF8] focus:bg-[#365DF8]/10 outline-none transition text-sm dark:bg-gray-900 disabled:opacity-50"
          />
        </div>

        <CButton
          type="submit"
          size="lg"
          className="w-full"
          disabled={loading || !otp || !newPassword || !confirmPassword}
        >
          {loading ? "Resetting..." : "Reset Password"}
        </CButton>
      </form>

      <div className="mt-2 text-sm text-muted-foreground flex gap-2">
        <CButton variant="link" size="sm" onClick={onChangeEmail}>
          Change email
        </CButton>
      </div>
    </>
  );
};

export default VerifyStep;
